import { clearChatInitInflight } from './leadGenChatSession'
import { normalizeLeadGenAnswers } from '../shared/leadGenAnswers'

const saveToStorage = (state) => {
  try {
    const dataToSave = {
      leadGenQuestions: state.leadGenQuestions,
      answers: state.answers,
      leadGenQuestionCount: state.leadGenQuestionCount,
      prequalQuestionCount: state.prequalQuestionCount,
      currentQuestionIndex: state.currentQuestionIndex,
      orgGuid: state.orgGuid,
      orgType: state.orgType,
      settings: state.settings,
      sac: state.sac,
      leadId: state.leadId,
      selectedLoanType: state.selectedLoanType,
      completionPageType: state.completionPageType,
      hasAccount: state.hasAccount,
      letterGuid: state.letterGuid,
      artifactGuid: state.artifactGuid,
      isInConversationalFlow: state.isInConversationalFlow,
      conversationalChatId: state.conversationalChatId,
      conversationalLeadData: state.conversationalLeadData,
      conversationalContactCollected: state.conversationalContactCollected,
      conversationalLeadComplete: state.conversationalLeadComplete,
      conversationalPrequalComplete: state.conversationalPrequalComplete,
      conversationalMessages: state.conversationalMessages,
      conversationalQuestionKeys: state.conversationalQuestionKeys,
      conversationalCurrentQuestionKey: state.conversationalCurrentQuestionKey,
      conversationalQuickReplies: state.conversationalQuickReplies,
      conversationalQuickRepliesAreSuggestions: state.conversationalQuickRepliesAreSuggestions,
      conversationalConsentSubmitted: state.conversationalConsentSubmitted
    }
    sessionStorage.setItem('leadGenFlow', JSON.stringify(dataToSave))
  } catch (error) {
    console.error('Failed to save to session storage:', error)
  }
}

const resetFormData = (state) => {
  state.leadGenQuestions = []
  state.answers = { contact_info: state.answers.contact_info || {} }
  state.currentQuestionIndex = 0
}

const clearSessionStorage = () => {
  try {
    sessionStorage.removeItem('leadGenFlow')
  } catch (error) {
    console.error('Failed to clear session storage:', error)
  }
}

export default {
  setAnswers (state, payload) {
    state.answers = normalizeLeadGenAnswers({ ...state.answers, ...payload })
    saveToStorage(state)
  },
  setQuestionFlowData (state, payload) {
    const previewQuestion = payload?.previewQuestion || false
    const questions = payload?.form?.questions
    const availableQuestions = questions?.filter(question => question.enabled || question.required)

    state.leadGenQuestions = previewQuestion ? questions : availableQuestions
    if (state.answers?.loan_type !== 'prequal') {
      state.leadGenQuestionCount = state.leadGenQuestions?.length || 0
    }
    saveToStorage(state)
  },
  setOrgGuid (state, payload) {
    state.orgGuid = payload
    saveToStorage(state)
  },
  setOrgId (state, payload) {
    state.orgId = payload
  },
  setOrgType (state, payload) {
    state.orgType = payload
    saveToStorage(state)
  },
  setSettings (state, payload) {
    state.settings = payload
    saveToStorage(state)
  },
  setCurrentQuestionIndex (state, payload) {
    state.currentQuestionIndex = payload
    saveToStorage(state)
  },
  setFormSubmitted (state, payload) {
    state.formSubmitted = payload
  },
  setSac (state, payload) {
    state.sac = payload
    saveToStorage(state)
  },
  setLeadId (state, payload) {
    state.leadId = payload
    saveToStorage(state)
  },
  setSelectedLoanType (state, payload) {
    state.selectedLoanType = payload
    saveToStorage(state)
  },
  resetAnswers (state) {
    state.answers = {}
  },
  setPreviewForm (state, payload) {
    state.previewForm = payload
  },
  setPreviewQuestion (state, payload) {
    state.previewQuestion = payload
  },
  setShowLandingPage (state, payload) {
    state.showLandingPage = payload
    if (payload) {
      state.hasVisitedLandingPage = true
    }
  },
  clearSession (state) {
    resetFormData(state)
    state.leadId = null
    state.leadGenQuestionCount = 0
    state.prequalQuestionCount = 0
    state.selectedLoanType = null
    clearSessionStorage()
  },
  setPartnerSettings (state, payload) {
    state.settings.partnerImageUrl = payload.partnerImageUrl
    state.settings.partnerInitials = payload.partnerInitials
  },
  setPartner (state, partner) {
    state.partner = partner
  },
  reinitializeServicerSettings (state) {
    state.settings.orgIcon = null
    state.settings.orgAppIcon = null
    state.settings.servicerImageUrl = null
    state.settings.servicerInitials = null
    state.settings.landingPageTitle = null
  },
  reinitializeOrgSettings (state) {
    state.settings.landingPageTitle = null
    state.settings.submissionPageBodyText = null
    state.settings.orgIcon = null
    state.settings.orgAppIcon = null
    state.settings.name = null
  },
  reinitializePartnerSettings (state) {
    state.settings.partnerImageUrl = null
    state.settings.partnerInitials = null
    state.partner = null
  },
  setConversationalFlow (state, isActive) {
    state.isInConversationalFlow = isActive
    saveToStorage(state)
  },
  setConversationalChatId (state, chatId) {
    state.conversationalChatId = chatId
    saveToStorage(state)
  },
  updateConversationalLeadData (state, data) {
    state.conversationalLeadData = { ...state.conversationalLeadData, ...data }
    saveToStorage(state)
  },
  setConversationalContactCollected (state, value) {
    state.conversationalContactCollected = value
    saveToStorage(state)
  },
  setConversationalLeadComplete (state, value) {
    state.conversationalLeadComplete = value
    saveToStorage(state)
  },
  setConversationalPrequalComplete (state, value) {
    state.conversationalPrequalComplete = value
    saveToStorage(state)
  },
  setConversationalMessages (state, messages) {
    state.conversationalMessages = messages
    saveToStorage(state)
  },
  setConversationalQuestionKeys (state, keys) {
    state.conversationalQuestionKeys = keys
    saveToStorage(state)
  },
  setConversationalCurrentQuestionKey (state, key) {
    state.conversationalCurrentQuestionKey = key
    saveToStorage(state)
  },
  setConversationalQuickReplies (state, { replies, areSuggestions }) {
    state.conversationalQuickReplies = replies
    state.conversationalQuickRepliesAreSuggestions = areSuggestions
    saveToStorage(state)
  },
  setConversationalConsentSubmitted (state, value) {
    state.conversationalConsentSubmitted = value
    saveToStorage(state)
  },
  clearConversationalSession (state) {
    state.conversationalWarmupEpoch = (state.conversationalWarmupEpoch || 0) + 1
    clearChatInitInflight()
    state.isInConversationalFlow = false
    state.conversationalChatId = null
    state.conversationalLeadData = {}
    state.conversationalContactCollected = false
    state.conversationalLeadComplete = false
    state.conversationalPrequalComplete = false
    state.conversationalMessages = []
    state.conversationalQuestionKeys = null
    state.conversationalCurrentQuestionKey = null
    state.conversationalQuickReplies = []
    state.conversationalQuickRepliesAreSuggestions = false
    state.conversationalConsentSubmitted = false
    saveToStorage(state)
  },
  setCompletionPageType (state, pageType) {
    state.completionPageType = pageType
    saveToStorage(state)
  },
  setHasAccount (state, hasAccount) {
    state.hasAccount = hasAccount
    saveToStorage(state)
  },
  setLetterGuid (state, letterGuid) {
    state.letterGuid = letterGuid
    saveToStorage(state)
  },
  setArtifactGuid (state, artifactGuid) {
    state.artifactGuid = artifactGuid
    saveToStorage(state)
  },
  setLeadGenQuestionCount (state, count) {
    state.leadGenQuestionCount = count
    saveToStorage(state)
  },
  setPrequalQuestionCount (state, questions) {
    const count = questions?.questions?.filter(q => q.enabled || q.required).length || 0
    state.prequalQuestionCount = count
    saveToStorage(state)
  },
  setServicer (state, servicer) {
    state.servicer = servicer
  },
  syncConversationalProgress (state, { shortFormCount, prequalCount, questionIndex }) {
    state.leadGenQuestionCount = shortFormCount
    state.prequalQuestionCount = prequalCount
    state.currentQuestionIndex = questionIndex
    saveToStorage(state)
  }
}
